import React from 'react';
import { Trash2, MapPin, Eye, Heart, CheckCircle2, Clock, XCircle, CreditCard as Edit } from 'lucide-react';
import { TravelProfile } from '../../../types/matching';
import { getAge } from '../../../utils/date';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { budgetCountries } from '../../../data/budget-countries';

interface ProfileHeroProps {
  profile: TravelProfile;
  isModal?: boolean;
  onModalClose?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  isDeletingProfile?: boolean;
}

export function ProfileHero({ profile, isModal, onModalClose, onEdit, onDelete, isDeletingProfile }: ProfileHeroProps) {
  const [cityPhoto, setCityPhoto] = React.useState<string | null>(null);

  const countryInfo = React.useMemo(() => {
    if (!profile.preferences.destination) return { flag: null, name: "destination inconnue" };
    const country = budgetCountries.find(c => c.code === profile.preferences.destination);
    return {
      flag: country?.flag || null,
      name: country?.name || "destination inconnue"
    };
  }, [profile.preferences.destination]);

  React.useEffect(() => {
    const fetchCachedPhoto = async () => {
      if (profile.preferences.city) {
        try {
          const cacheRef = doc(db, 'photo_cache', profile.preferences.city.toLowerCase());
          const cacheDoc = await getDoc(cacheRef);
          if (cacheDoc.exists()) {
            const data = cacheDoc.data();
            const photos = data.photos;
            if (photos && photos.length > 0) {
              const randomPhoto = photos[Math.floor(Math.random() * photos.length)];
              setCityPhoto(randomPhoto.url);
            }
          }
        } catch (error) {
          console.error('Error fetching cached photo:', error);
        }
      }
    };
    fetchCachedPhoto();
  }, [profile.preferences.city]);

  const pvtStatusConfig = {
    obtained: { label: 'PVT obtenu', icon: CheckCircle2, classes: 'bg-emerald-500/90 text-white' },
    pending: { label: 'PVT en cours', icon: Clock, classes: 'bg-amber-500/90 text-white' },
    'not-requested': { label: 'PVT non demande', icon: XCircle, classes: 'bg-white/20 text-white' },
  };
  const status = pvtStatusConfig[profile.pvtStatus] || pvtStatusConfig['not-requested'];
  const StatusIcon = status.icon;

  const heroImage = cityPhoto || getDestinationImage(profile.preferences.destination);
  const hasUserPhoto = profile.photos && profile.photos.length > 0;
  const avatarSrc = hasUserPhoto ? profile.photos[0] : heroImage;

  return (
    <div className="relative h-[420px] sm:h-[480px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
      </div>

      {(onEdit || onDelete) && (
        <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
          {onEdit && (
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-3.5 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-xl transition-all text-sm font-medium shadow-lg"
            >
              <Edit className="w-4 h-4" />
              <span>Modifier</span>
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              disabled={isDeletingProfile}
              className="flex items-center gap-2 px-3.5 py-2 bg-red-600/80 hover:bg-red-600 backdrop-blur-sm text-white rounded-xl transition-all text-sm font-medium shadow-lg disabled:opacity-50"
            >
              {isDeletingProfile ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              <span>Supprimer</span>
            </button>
          )}
        </div>
      )}

      <div className="relative h-full flex items-end">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 sm:pb-28">
          <div className="flex flex-col sm:flex-row items-center sm:items-end gap-5">
            <div className="relative flex-shrink-0">
              <div className="w-28 sm:w-36 h-28 sm:h-36 rounded-2xl overflow-hidden border-[3px] border-white/90 shadow-2xl bg-gray-200">
                <img
                  src={avatarSrc}
                  alt={profile.firstName}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1 text-center sm:text-left space-y-2.5 pb-1">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold backdrop-blur-sm ${status.classes}`}>
                  <StatusIcon className="w-3.5 h-3.5" />
                  {status.label}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/15 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold">
                  {countryInfo.flag && <span className="text-base">{countryInfo.flag}</span>}
                  {profile.preferences.city}
                </span>
              </div>

              <h1 className="text-2xl sm:text-4xl font-bold text-white tracking-tight">
                {profile.firstName}{getAge(profile.birthDate) ? `, ${getAge(profile.birthDate)} ans` : ''}
              </h1>

              <div className="flex items-center justify-center sm:justify-start gap-4 text-white/70 text-sm">
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{profile.location}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Eye className="w-3.5 h-3.5" />
                  <span>{profile.viewCount || 0} vues</span>
                </div>
                {profile.likes && profile.likes.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <Heart className="w-3.5 h-3.5" />
                    <span>{profile.likes.length}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const getDestinationImage = (countryCode: string): string => {
  if (!countryCode) return 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?q=80&w=2670&auto=format&fit=crop';
  const countryImages: Record<string, string> = {
    'aus': 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?q=80&w=2670&auto=format&fit=crop',
    'nz': 'https://images.unsplash.com/photo-1507699622108-4be3abd695ad?q=80&w=2670&auto=format&fit=crop',
    'ca': 'https://images.unsplash.com/photo-1503614472-8c93d56e92ce?q=80&w=2670&auto=format&fit=crop',
    'jpn': 'https://images.unsplash.com/photo-1480796927426-f609979314bd?q=80&w=2670&auto=format&fit=crop',
    'kor': 'https://images.unsplash.com/photo-1517154421773-0529f29ea451?q=80&w=2670&auto=format&fit=crop',
    'twn': 'https://images.unsplash.com/photo-1470004914212-05527e49370b?q=80&w=2670&auto=format&fit=crop',
    'arg': 'https://images.unsplash.com/photo-1612294037637-ec328d0e075e?q=80&w=2670&auto=format&fit=crop',
    'chl': 'https://images.unsplash.com/photo-1546800273-4d63ff843853?q=80&w=2670&auto=format&fit=crop',
    'col': 'https://images.unsplash.com/photo-1576018753502-1a55b66cc44a?q=80&w=2670&auto=format&fit=crop',
    'mex': 'https://images.unsplash.com/photo-1518105779142-d975f22f1b0a?q=80&w=2670&auto=format&fit=crop',
    'per': 'https://images.unsplash.com/photo-1526392060635-9d6019884377?q=80&w=2670&auto=format&fit=crop',
    'uru': 'https://images.unsplash.com/photo-1624416254374-b471bfcaac81?q=80&w=2670&auto=format&fit=crop',
    'bra': 'https://images.unsplash.com/photo-1483729558449-99ef09a8c325?q=80&w=2670&auto=format&fit=crop',
    'hkg': 'https://images.unsplash.com/photo-1536599018102-9f803c140fc1?q=80&w=2670&auto=format&fit=crop',
    'fra': 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=2670&auto=format&fit=crop',
    'ecu': 'https://images.unsplash.com/photo-1504457047772-27faf1c00561?q=80&w=2670&auto=format&fit=crop'
  };
  return countryImages[countryCode] || 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?q=80&w=2670&auto=format&fit=crop';
};
